<div class="user-edit-page">
  @if (isLoading()) {
    <zag-loading-state [message]="'common.loading' | translate" />
  } @else if (error()) {
    <zag-error-state [message]="error()!" [backLink]="'/settings/users'" [backText]="'common.back' | translate" />
  } @else {
    <ng-template zagPageActions>
      <button zagButton type="button" [disabled]="isSaving() || !hasPendingChanges()" (click)="saveAll()">
        {{ 'common.save' | translate }}
      </button>
    </ng-template>

    <zag-entity-detail-layout>
      <div slot="sidebar">
        <!-- Profile Card (no stats) -->
        <zag-profile-summary-card
          [avatarName]="userName()"
          [avatarSrc]="userImage()"
          [subtitle]="userEmail()"
          [quickActionsTitle]="'common.quickActions' | translate"
          [quickActions]="quickActions()"
          [dangerActions]="dangerActions()"
          (quickActionClick)="onQuickAction($event)"
          (dangerActionClick)="onDangerAction($event)"
        />

        <!-- Linked Person Card -->
        <div class="user-edit-page__sidebar-card">
          <h3 class="user-edit-page__card-title">
            {{ 'settings.users.quickActions.linkPerson' | translate }}
          </h3>
          <zag-dropdown
            [options]="allPersonOptions()"
            [placeholder]="'settings.users.selectPerson' | translate"
            [value]="displayedPersonId()"
            [clearable]="true"
            [disabled]="unlinkedPersonsLoading() || allPersonOptions().length === 0"
            (selectionChange)="onPersonSelectionChange($event)"
          />
          @if (allPersonOptions().length === 0 && !unlinkedPersonsLoading()) {
            <p class="user-edit-page__empty-hint">
              {{ 'settings.users.noUnlinkedPersons' | translate }}
            </p>
          }
          @if (hasUnsavedPersonChange()) {
            <p class="user-edit-page__pending-hint">
              {{ 'settings.users.personChangeStaged' | translate }}
            </p>
          }
        </div>

        <!-- Inherited Roles Card -->
        <div class="user-edit-page__sidebar-card">
          <h3 class="user-edit-page__card-title">
            {{ 'settings.users.sections.inheritedRoles' | translate }}
          </h3>
          <div class="user-edit-page__card-list">
            @for (role of inheritedRoles(); track role.id) {
              <div class="user-edit-page__card-item">
                <span zagIcon="security" class="user-edit-page__card-item-icon"></span>
                <span class="user-edit-page__card-item-text">{{ role.id }}</span>
              </div>
            } @empty {
              <p class="user-edit-page__empty-hint">
                {{ 'settings.users.noInheritedRoles' | translate }}
              </p>
            }
          </div>
        </div>

        <!-- Effective Permissions Card -->
        <div class="user-edit-page__sidebar-card">
          <h3 class="user-edit-page__card-title">
            {{ 'settings.users.sections.effectivePermissions' | translate }}
          </h3>
          <div class="user-edit-page__card-list user-edit-page__card-list--tall">
            @for (permission of effectivePermissions(); track permission.id) {
              <div class="user-edit-page__card-item">
                <span zagIcon="check" class="user-edit-page__card-item-icon user-edit-page__card-item-icon--success"></span>
                <span class="user-edit-page__card-item-text">{{ permission.id }}</span>
              </div>
            } @empty {
              <p class="user-edit-page__empty-hint">
                {{ 'settings.users.noPermissions' | translate }}
              </p>
            }
          </div>
        </div>
      </div>

      <div slot="content">
        <!-- Accordion with Direct Roles and Groups -->
        <zag-accordion>
          <!-- Direct Roles Section -->
          <zag-accordion-item [title]="'settings.users.sections.directRoles' | translate" icon="security" [expanded]="true">
            <div class="user-edit-page__section">
              <p class="user-edit-page__section-description">
                {{ 'settings.users.directRolesDescription' | translate }}
              </p>

              @if (rolesLoading()) {
                <div class="user-edit-page__loading">
                  <span zagIcon="sync" class="spinning"></span>
                  <p>{{ 'common.loading' | translate }}</p>
                </div>
              } @else {
                <div class="user-edit-page__role-list">
                  @for (role of availableRoles(); track role.id) {
                    <zag-selection-row
                      [label]="role.id"
                      [description]="role.description ?? null"
                      [checked]="isRoleSelected(role.id)"
                      [changed]="isRoleChanged(role.id)"
                      (checkedChange)="setRoleSelection(role.id, $event)"
                    />
                  } @empty {
                    <p class="user-edit-page__empty">
                      {{ 'settings.users.noRolesAvailable' | translate }}
                    </p>
                  }
                </div>
              }
            </div>
          </zag-accordion-item>

          <!-- Groups Section -->
          <zag-accordion-item [title]="'settings.users.sections.groups' | translate" icon="groups" [expanded]="true">
            <div class="user-edit-page__section">
              <!-- Add Group Dropdown -->
              <zag-dropdown
                [options]="unassignedGroupOptions()"
                [placeholder]="'settings.users.addGroup' | translate"
                [value]="selectedGroupToAdd()"
                [disabled]="unassignedGroupsLoading() || unassignedGroupOptions().length === 0"
                (selectionChange)="onGroupToAddSelected($event)"
              />
              @if (unassignedGroupOptions().length === 0 && !unassignedGroupsLoading()) {
                <p class="user-edit-page__no-items">
                  {{ 'settings.users.noUnassignedGroups' | translate }}
                </p>
              }

              <!-- Groups List -->
              <div class="user-edit-page__list">
                @if (groupsLoading()) {
                  <div class="user-edit-page__loading">
                    <span zagIcon="sync" class="spinning"></span>
                    <p>{{ 'common.loading' | translate }}</p>
                  </div>
                } @else {
                  @for (group of allGroups(); track group.id) {
                    <div class="user-edit-page__item" [class.user-edit-page__item--local]="isLocalGroupAddition(group)">
                      <span zagIcon="groups" class="user-edit-page__icon"></span>
                      <div class="user-edit-page__item-info">
                        <span class="user-edit-page__item-name">{{ group.name }}</span>
                        @if (group.description) {
                          <span class="user-edit-page__item-description">{{ group.description }}</span>
                        }
                      </div>
                      @if (group.isAdmin) {
                        <span class="user-edit-page__admin-badge">
                          {{ 'settings.users.adminBadge' | translate }}
                        </span>
                      }
                      @if (isLocalGroupAddition(group)) {
                        <span class="user-edit-page__badge">
                          {{ 'settings.users.pendingBadge' | translate }}
                        </span>
                      }
                      <button
                        type="button"
                        zagButton="icon"
                        zagType="error"
                        zagSize="sm"
                        [title]="'common.remove' | translate"
                        [attr.aria-label]="'common.remove' | translate"
                        (click)="removeGroup(group)"
                      >
                        <span zagIcon="delete"></span>
                      </button>
                    </div>
                  } @empty {
                    <p class="user-edit-page__empty">
                      {{ 'settings.users.noGroups' | translate }}
                    </p>
                  }
                }
              </div>
            </div>
          </zag-accordion-item>
        </zag-accordion>

        @if (userId() && showEntityAccessPanel()) {
          <div class="user-edit-page__access-panel">
            <sc-user-entity-access-panel [userId]="userId()!" [userLabel]="userName()" />
          </div>
        }
      </div>
    </zag-entity-detail-layout>

    @if (userId() && canAccessAuditLog()) {
      <zag-audit-log-panel
        schema="auth"
        table="users"
        [recordId]="userId()!"
        [open]="auditPanelOpen()"
        (openChange)="auditPanelOpen.set($event)"
      />
    }
  }
</div>
